import React from 'react';
import { Header } from '../components/Header';
import './LegalScreen.css';

export const TermsOfServiceScreen: React.FC = () => {
  return (
    <div className="legal-screen">
      <div className="legal-container">
        <Header title="Terms of Service" />
        <p className="legal-date" style={{ textAlign: 'center', marginTop: '16px', color: 'var(--color-text-secondary)' }}>Effective Date: October 24, 2025</p>

        <div className="legal-content">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              Welcome to Alveon Financial Tracker ("Alveon", "we", "our", or "us"). 
              By accessing or using our application, you agree to be bound by these Terms of Service 
              ("Terms"). If you do not agree to these Terms, please do not use our services.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Alveon. 
              Please read them carefully.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>Alveon is a personal financial tracking application that provides:</p>
            <ul>
              <li>Income and expense tracking</li>
              <li>Budget management and monitoring</li>
              <li>Financial goal setting and progress tracking</li>
              <li>AI-powered financial insights and recommendations</li>
              <li>Data synchronization across devices</li>
              <li>Customizable notifications and reminders</li>
              <li>Data export and reporting features</li>
            </ul>
          </section>

          <section>
            <h2>3. User Accounts</h2>
            
            <h3>3.1 Account Creation</h3>
            <p>To use Alveon, you must create an account by providing:</p>
            <ul>
              <li>A valid email address or Telegram account</li>
              <li>Authentication credentials (password or OAuth)</li>
              <li>Accurate and complete information</li>
            </ul>

            <h3>3.2 Account Security</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
              <li>Using strong, unique passwords</li>
            </ul>

            <h3>3.3 Account Restrictions</h3>
            <ul>
              <li>You must be at least 13 years old to use Alveon</li>
              <li>One person or entity may maintain only one account</li>
              <li>Accounts are non-transferable</li>
            </ul>
          </section>

          <section>
            <h2>4. User Conduct and Prohibited Activities</h2>
            
            <h3>4.1 Acceptable Use</h3>
            <p>You agree to use Alveon only for lawful purposes and in accordance with these Terms.</p>

            <h3>4.2 Prohibited Activities</h3>
            <p>You agree NOT to:</p>
            <ul>
              <li>Use the service for any illegal or unauthorized purpose</li>
              <li>Violate any laws or regulations in your jurisdiction</li>
              <li>Infringe on intellectual property rights</li>
              <li>Transmit viruses, malware, or harmful code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the service or servers</li>
              <li>Use automated systems (bots, scrapers) without permission</li>
              <li>Impersonate another person or entity</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Collect or harvest user data without consent</li>
              <li>Reverse engineer or decompile the application</li>
              <li>Resell or commercialize the service</li>
            </ul>
          </section>

          <section>
            <h2>5. Subscription and Payment</h2>
            
            <h3>5.1 Free and Premium Features</h3>
            <p>Alveon offers both free and premium subscription tiers:</p>
            <ul>
              <li><strong>Free Plan:</strong> Basic financial tracking features</li>
              <li><strong>Premium Plans:</strong> Advanced features, increased limits, AI insights</li>
            </ul>

            <h3>5.2 Payment Terms</h3>
            <ul>
              <li>Premium subscriptions are billed in advance on a monthly or annual basis</li>
              <li>Payments are processed through secure third-party payment providers</li>
              <li>All fees are in the currency specified at checkout</li>
              <li>Prices are subject to change with 30 days notice</li>
            </ul>

            <h3>5.3 Refunds</h3>
            <ul>
              <li>Monthly subscriptions: No refunds for partial months</li>
              <li>Annual subscriptions: Pro-rated refunds within 14 days of purchase</li>
              <li>Refund requests: Contact support@alveon.app</li>
            </ul>

            <h3>5.4 Cancellation</h3>
            <p>
              You may cancel your subscription at any time. Upon cancellation, you will retain 
              access until the end of your billing period. No refunds for remaining time.
            </p>
          </section>

          <section>
            <h2>6. Intellectual Property Rights</h2>
            
            <h3>6.1 Our Rights</h3>
            <p>
              Alveon and its original content, features, and functionality are owned by us and 
              protected by international copyright, trademark, patent, trade secret, and other 
              intellectual property laws.
            </p>

            <h3>6.2 Your Rights</h3>
            <p>
              You retain all rights to the financial data you enter into Alveon. By using our 
              service, you grant us a limited license to process and display your data to provide 
              our services.
            </p>

            <h3>6.3 Restrictions</h3>
            <p>You may not:</p>
            <ul>
              <li>Copy, modify, or distribute our software</li>
              <li>Remove any copyright or proprietary notices</li>
              <li>Use our trademarks without written permission</li>
            </ul>
          </section>

          <section>
            <h2>7. Data Accuracy and Responsibility</h2>
            
            <h3>7.1 Your Responsibility</h3>
            <p>You are solely responsible for:</p>
            <ul>
              <li>The accuracy of data you enter into Alveon</li>
              <li>Verifying all calculations and reports</li>
              <li>Making financial decisions based on the information</li>
              <li>Complying with tax and accounting regulations</li>
            </ul>

            <h3>7.2 Not Financial Advice</h3>
            <p>
              Alveon provides tools for financial tracking and insights, but does NOT provide 
              financial, tax, or legal advice. Always consult qualified professionals for important 
              financial decisions.
            </p>
          </section>

          <section>
            <h2>8. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Our collection and use of personal information 
              is governed by our <a href="/privacy">Privacy Policy</a>, which is incorporated 
              into these Terms by reference.
            </p>
            <p>Key points:</p>
            <ul>
              <li>We do not sell your personal data</li>
              <li>Your financial data is encrypted and secure</li>
              <li>You can export or delete your data at any time</li>
            </ul>
          </section>

          <section>
            <h2>9. Service Availability and Modifications</h2>
            
            <h3>9.1 Availability</h3>
            <p>
              We strive to provide reliable service, but we do not guarantee uninterrupted access. 
              The service may be temporarily unavailable due to maintenance, updates, or technical issues.
            </p>

            <h3>9.2 Modifications</h3>
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the service at any 
              time with or without notice. We are not liable for any modifications or discontinuation.
            </p>

            <h3>9.3 Updates</h3>
            <p>
              We may release updates to improve functionality, fix bugs, or add features. Some updates 
              may be required for continued use.
            </p>
          </section>

          <section>
            <h2>10. Termination</h2>
            
            <h3>10.1 By You</h3>
            <p>
              You may terminate your account at any time by deleting it through the app settings 
              or contacting support.
            </p>

            <h3>10.2 By Us</h3>
            <p>We may terminate or suspend your account immediately if:</p>
            <ul>
              <li>You violate these Terms</li>
              <li>You engage in fraudulent or illegal activity</li>
              <li>Your account has been inactive for an extended period</li>
              <li>We are required to do so by law</li>
            </ul>

            <h3>10.3 Effect of Termination</h3>
            <p>Upon termination:</p>
            <ul>
              <li>Your access to the service will cease immediately</li>
              <li>Your data may be deleted from our servers</li>
              <li>You must cease all use of our service</li>
              <li>Provisions that should survive termination will remain in effect</li>
            </ul>
          </section>

          <section>
            <h2>11. Disclaimers and Limitation of Liability</h2>
            
            <h3>11.1 "AS IS" Service</h3>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, 
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h3>11.2 No Warranty</h3>
            <p>We do not warrant that:</p>
            <ul>
              <li>The service will be error-free or uninterrupted</li>
              <li>Defects will be corrected</li>
              <li>The service is free of viruses or harmful components</li>
              <li>Results will be accurate or reliable</li>
            </ul>

            <h3>11.3 Limitation of Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ALVEON SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, 
              REVENUE, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
            <p>
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS 
              PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
            </p>
          </section>

          <section>
            <h2>12. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Alveon, its officers, directors, 
              employees, and agents from any claims, damages, losses, liabilities, and expenses 
              (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use of the service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your content or data</li>
            </ul>
          </section>

          <section>
            <h2>13. Dispute Resolution</h2>
            
            <h3>13.1 Informal Resolution</h3>
            <p>
              Before filing a claim, please contact us at support@alveon.app to attempt to resolve 
              the dispute informally.
            </p>

            <h3>13.2 Arbitration</h3>
            <p>
              If informal resolution fails, disputes will be resolved through binding arbitration 
              rather than in court, except where prohibited by law.
            </p>

            <h3>13.3 Class Action Waiver</h3>
            <p>
              You agree to resolve disputes on an individual basis and waive any right to participate 
              in class actions or class arbitrations.
            </p>
          </section>

          <section>
            <h2>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of your 
              jurisdiction, without regard to conflict of law provisions.
            </p>
          </section>

          <section>
            <h2>15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of 
              material changes by:
            </p>
            <ul>
              <li>Posting a notice in the app</li>
              <li>Sending an email to your registered address</li>
              <li>Updating the "Effective Date" at the top of this page</li>
            </ul>
            <p>
              Your continued use of the service after changes constitutes acceptance of the 
              updated Terms. If you do not agree, you must stop using the service.
            </p>
          </section>

          <section>
            <h2>16. Miscellaneous</h2>
            
            <h3>16.1 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement 
              between you and Alveon.
            </p>

            <h3>16.2 Severability</h3>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining 
              provisions will remain in full force and effect.
            </p>

            <h3>16.3 Waiver</h3>
            <p>
              Our failure to enforce any right or provision shall not constitute a waiver of 
              that right or provision.
            </p>

            <h3>16.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms or your account without our written consent. 
              We may assign our rights and obligations without restriction.
            </p>

            <h3>16.5 Force Majeure</h3>
            <p>
              We are not liable for delays or failures in performance due to causes beyond our 
              reasonable control, including natural disasters, war, terrorism, riots, or internet failures.
            </p>
          </section>

          <section>
            <h2>17. Contact Information</h2>
            <p>For questions about these Terms or our service, contact us:</p>
            <ul className="contact-info">
              <li><strong>Email:</strong> support@alveon.app</li>
              <li><strong>Telegram:</strong> @AlveonSupport</li>
              <li><strong>Website:</strong> <a href="https://app.alveon.app" target="_blank" rel="noopener noreferrer">https://app.alveon.app</a></li>
            </ul>
          </section>

          <section>
            <h2>18. Acknowledgment</h2>
            <p>
              BY USING ALVEON, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE 
              BOUND BY THESE TERMS OF SERVICE.
            </p>
          </section>
        </div>

        <footer className="legal-footer">
          <p>Last updated: October 24, 2025</p>
          <p>© 2025 Alveon Financial Tracker. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

